import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setSpotOrder, reorderSpots } from '../store/slices/savedSpotsSlice';
import DragHandleIcon from './icons/DragHandleIcon';

// Touch + mouse drag-to-reorder list, persisted via reorderSpots on drop.
const ReorderableSpotsList = ({ spots }) => {
    const dispatch = useDispatch();
    const [order, setOrder] = useState(spots.map((spot) => spot.id));
    const [draggingId, setDraggingId] = useState(null);
    const itemRefs = useRef({});

    useEffect(() => {
        if (draggingId !== null) return;
        setOrder(spots.map((spot) => spot.id));
    }, [spots, draggingId]);

    const spotsById = useMemo(() => {
        const map = {};
        spots.forEach((spot) => { map[spot.id] = spot; });
        return map;
    }, [spots]);

    const handlePointerMove = useCallback((e) => {
        setDraggingId((currentDraggingId) => {
            if (currentDraggingId === null) return currentDraggingId;

            const pointerY = e.clientY;
            let targetIndex = -1;

            setOrder((currentOrder) => {
                targetIndex = currentOrder.findIndex((id) => {
                    const el = itemRefs.current[id];
                    if (!el) return false;
                    const rect = el.getBoundingClientRect();
                    return pointerY >= rect.top && pointerY <= rect.bottom;
                });

                const draggingIndex = currentOrder.indexOf(currentDraggingId);
                if (targetIndex === -1 || draggingIndex === -1 || draggingIndex === targetIndex) {
                    return currentOrder;
                }

                const newOrder = [...currentOrder];
                newOrder.splice(draggingIndex, 1);
                newOrder.splice(targetIndex, 0, currentDraggingId);
                return newOrder;
            });

            return currentDraggingId;
        });
    }, []);

    const handlePointerUp = useCallback(() => {
        setDraggingId((currentDraggingId) => {
            if (currentDraggingId === null) return null;
            setOrder((currentOrder) => {
                dispatch(setSpotOrder(currentOrder));
                dispatch(reorderSpots(currentOrder));
                return currentOrder;
            });
            return null;
        });
    }, [dispatch]);

    return (
        <div className="rounded-xl bg-custom-charcoal/30 divide-y divide-custom-charcoal overflow-hidden">
            {order.map((id) => {
                const spot = spotsById[id];
                if (!spot) return null;
                return (
                    <div
                        key={id}
                        ref={(el) => { itemRefs.current[id] = el; }}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${draggingId === id ? 'bg-custom-charcoal/60' : ''}`}
                    >
                        <button
                            type="button"
                            onPointerDown={(e) => {
                                setDraggingId(id);
                                e.currentTarget.setPointerCapture(e.pointerId);
                            }}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                            aria-label={`Drag to reorder ${spot.name}`}
                            className="cursor-grab active:cursor-grabbing text-custom-light-grey touch-none"
                        >
                            <DragHandleIcon />
                        </button>
                        <span className="text-sm">{spot.name}</span>
                    </div>
                );
            })}
        </div>
    );
};

export default ReorderableSpotsList;
